import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import course1 from '../../images/course1.jpg';
import course2 from '../../images/course2.jpg';
import Statistics from '../../images/Statistics.jpg';
import productengineeringandservices from '../../images/productengineeringandservices.png';
import './FeaturedCourses.css';
import { apiFetch } from '../../utils/api';

const courseImages = [course1, course2, Statistics, productengineeringandservices];

const FeaturedCourses = () => {
  const [courses, setCourses] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedCourses = async () => {
      try {
        const [coursesData, summaryData] = await Promise.all([
          apiFetch(`${window._env_.API_BASE_URL}/dashboard/featured-courses`),
          apiFetch(`${window._env_.API_BASE_URL}/courses/summary`),
        ]);
        setCourses(coursesData);
        setSummary(summaryData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchFeaturedCourses();
  }, []);

  useEffect(() => {
    const fetchEnrolledCourses = async () => {
      try {
        const userEmail = localStorage.getItem('userEmail');
        if (!userEmail) return;

        const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
        const enrolledList = await apiFetch(`${window._env_.API_BASE_URL}/users/${userInfo.user_id}/courses`);
        const enrolledIds = enrolledList.map((course) => course.course_id);
        setEnrolledCourseIds(enrolledIds);
      } catch (err) {
        console.error('Error fetching enrolled courses for featured section:', err);
      }
    };

    fetchEnrolledCourses();
  }, []);

  const handleEnroll = async (courseId) => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        navigate('/login');
        return;
      }

      const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
      const userId = userInfo.user_id;

      const enrollResponse = await apiFetch(`${window._env_.API_BASE_URL}/users/${userId}/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: courseId }),
      });

      const snackbar = document.createElement('div');
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); color: white; padding: 12px 24px; border-radius: 4px; z-index: 1000;';

      if (enrollResponse.detail?.includes('already enrolled')) {
        snackbar.textContent = 'User already enrolled';
        snackbar.style.backgroundColor = '#f44336';
        setEnrolledCourseIds(prev => (prev.includes(courseId) ? prev : [...prev, courseId]));
      } else {
        snackbar.textContent = 'Successfully enrolled in the course';
        snackbar.style.backgroundColor = '#4CAF50';
        setEnrolledCourseIds(prev => (prev.includes(courseId) ? prev : [...prev, courseId]));
      }

      document.body.appendChild(snackbar);
      setTimeout(() => {
        snackbar.remove();
        if (!enrollResponse.detail?.includes('already enrolled')) {
          navigate('/course-videos', {
            state: { department: courses.find(c => c.course_id === courseId)?.department_name || 'General', courseId }
          });
        }
      }, 3000);
    } catch (error) {
      console.error('Enrollment error:', error);
      const snackbar = document.createElement('div');
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); color: white; padding: 12px 24px; border-radius: 4px; z-index: 1000;';

      // Check if error is due to user already being enrolled
      if (error.message?.includes('already enrolled')) {
        snackbar.textContent = 'User already enrolled';
        snackbar.style.backgroundColor = '#f44336';
      } else {
        snackbar.textContent = 'An error occurred during enrollment. Please try again.';
        snackbar.style.backgroundColor = '#f44336';
      }

      document.body.appendChild(snackbar);
      setTimeout(() => snackbar.remove(), 3000);
    }
  };

  const handleUnenroll = async (courseId) => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        navigate('/login');
        return;
      }

      const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
      await apiFetch(
        `${window._env_.API_BASE_URL}/users/unenroll?user_id=${userInfo.user_id}&course_id=${courseId}`,
        { method: 'DELETE' }
      );

      setEnrolledCourseIds(prev => prev.filter(id => id !== courseId));

      const snackbar = document.createElement('div');
      snackbar.textContent = 'Successfully unenrolled from the course';
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); color: white; padding: 12px 24px; border-radius: 4px; background-color: #f44336; z-index: 1000;';
      document.body.appendChild(snackbar);
      setTimeout(() => snackbar.remove(), 3000);
    } catch (error) {
      console.error('Unenroll error:', error);
      const snackbar = document.createElement('div');
      snackbar.textContent = 'An error occurred while unenrolling. Please try again.';
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); color: white; padding: 12px 24px; border-radius: 4px; background-color: #f44336; z-index: 1000;';
      document.body.appendChild(snackbar);
      setTimeout(() => snackbar.remove(), 3000);
    }
  };

  if (loading) {
    return <div className="featured-courses">Loading...</div>;
  }

  if (error) {
    return <div className="featured-courses">Error: {error}</div>;
  }

  return (
    <div className="featured-courses">
      <h2>Popular Courses</h2>
      <div className="fc-course-container">
        <div className="fc-course-grid">
          {courses.map((course, index) => {
            const summaryData = summary[course.course_id] || { video_count: 0, total_duration: 'N/A' };
            return (
              <div className="fc-course-card" key={course.course_id}>
                <div className="fc-course-image" style={{ backgroundImage: `url(${courseImages[index % courseImages.length]})` }}>
                  <span className="fc-course-category">{course.department_name || 'General'}</span>
                </div>
                <div className="fc-course-content">
                  <span>{course.course_level || 'Beginner'}</span>
                  <h3>{course.course_title || 'Airflow Beginner'}</h3>
                  <p>
                    <span className="fc-course-detail">🎥 {summaryData.video_count || 0} Videos</span><br />
                    <span className="fc-course-detail">⏱ {summaryData.total_duration || 'N/A'} </span>
                  </p>
                  {enrolledCourseIds.includes(course.course_id) ? (
                    <div className="fc-course-action-group">
                      <Link
                        to="/course-videos"
                        state={{
                          department: course.department_name || 'General',
                          courseId: course.course_id,
                          courseTitle: course.course_title
                        }}
                        className="continue-btn"
                      >
                        Continue
                      </Link>
                      <button
                        className="btn-unenroll"
                        onClick={() => handleUnenroll(course.course_id)}
                      >
                        Unenroll
                      </button>
                    </div>
                  ) : (
                    <button className="btn-enroll" onClick={() => handleEnroll(course.course_id)}>Enroll Now</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FeaturedCourses;